/* The Flow is:
1. Fetch 7 days of data → costWindows array
2. Convert to analytics format → analyticsData array
3. Count unique rates → addRateCategoriesToRecords does this
4. Apply the rules → Based on count (1, 2, 3, or >3)
5. Categorize each record → CHEAP, DAY, or PEAK
6. Extract final rates → Average/min/max for each category
*/

import { getTariffsTOUCosts } from '../../api/Optimise';
import type { CostWindow } from '../../types/optimise';
import type { AnalyticsRecord, TariffRates } from '../../types/tariff';
import { getUnixTime, startOfUKDay, subDays } from '../../utils/dateUtils';
import { addRateCategoriesToRecords, RateCategories } from '../../utils/savingsUtils';

type RateCategoryKey = 'import_rate_category' | 'export_rate_category';
type RateType = 'import_rate' | 'export_rate';

/**
 * Fetches and processes import tariff rates for a given tariff ID
 *
 * @param tariffId
 * @param setImportTariffRates - Function to update the import tariff rates state
 * @param setLoadingTariffRates - Function to update the loading state
 */
export const fetchImportTariffRates = async (
  tariffId: string,
  setImportTariffRates: (rates: TariffRates | null) => void,
  setLoadingTariffRates: (loading: boolean) => void
): Promise<void> => {
  // If no tariff ID is provided, clear the rates and return early
  if (!tariffId) {
    setImportTariffRates(null);
    return;
  }

  // Start loading state
  setLoadingTariffRates(true);

  try {
    // Calculate the time range for fetching tariff data
    // We fetch data for a 7-day period starting from a week ago
    const end = startOfUKDay();
    const endTimestampUTC = getUnixTime(end);
    const start = subDays(end, 7);
    const startTimestampUTC = getUnixTime(start);

    // Fetch the time-of-use costs from the API
    const touCosts = await getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, tariffId);

    // Process the cost windows to extract different rate types (cheap/day/peak)
    const processedData: TariffRates | null = processImportTariffRates(touCosts.cost_windows);
    setImportTariffRates(processedData);
    setImportTariffRates(processedData);
  } catch (error) {
    console.error('Error fetching tariff rates:', error);
    setImportTariffRates(null);
  } finally {
    // Always stop loading, even if there was an error
    setLoadingTariffRates(false);
  }
};

/**
 * Fetches and processes export tariff rates for a given tariff ID
 *
 * @param tariffId
 * @param setExportTariffRates - Function to update the export tariff rates state
 * @param setLoadingExportTariffRates - Function to update the loading state
 */
export const fetchExportTariffRates = async (
  tariffId: string,
  setExportTariffRates: (rates: TariffRates | null) => void,
  setLoadingExportTariffRates: (loading: boolean) => void
): Promise<void> => {
  // If no tariff ID is provided, clear the rates and return early
  if (!tariffId) {
    setExportTariffRates(null);
    return;
  }

  // Start loading state
  setLoadingExportTariffRates(true);

  try {
    // Calculate the time range for fetching tariff data
    // We fetch data for a 7-day period starting from a week ago
    const end = startOfUKDay();
    const endTimestampUTC = getUnixTime(end);
    const start = subDays(end, 7);
    const startTimestampUTC = getUnixTime(start);

    // Fetch the time-of-use costs from the API
    const touCosts = await getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, tariffId);

    // Process the cost windows to extract different rate types (cheap/day/peak)
    const processedData = processExportTariffRates(touCosts.cost_windows);
    setExportTariffRates(processedData);
  } catch (error) {
    console.error('Error fetching export tariff rates:', error);
    setExportTariffRates(null);
  } finally {
    // Always stop loading, even if there was an error
    setLoadingExportTariffRates(false);
  }
};

/**
 * Processes tariff cost windows to extract cheap, day, and peak rates
 *
 * This function takes the raw cost windows from the API and:
 * 1. Converts them to the format expected by the rate categorisation utility
 * 2. Uses the utility to categorise rates as CHEAP, DAY, or PEAK
 * 3. Extracts the rates for each category
 * 4. Calculates the final tariff rates (day average, cheap minimum, peak maximum)
 *
 * @param costWindows - Array of cost windows from the API
 * @param rateType - Either 'import' or 'export' to specify which type of rates to process
 * @returns Processed tariff rates or null if no data
 */
export const processTariffRates = (costWindows: CostWindow[], rateType: 'import' | 'export'): TariffRates | null => {
  // If no cost windows provided, return null
  if (!costWindows || costWindows.length === 0) {
    return null;
  }

  const rateCategoryKey = `${rateType}_rate_category` as RateCategoryKey;
  const rateKey = `${rateType}_rate` as RateType;

  // Step 1: Convert cost windows to analytics format and collect all rates in a single pass
  // This combines two operations: creating analyticsData and collecting allRates
  const { analyticsData, allRates } = costWindows.reduce(
    (accumulator, costWindow) => {
      // Build the analytics record for rate categorization
      accumulator.analyticsData.push({
        [rateType === 'import' ? 'import_rate' : 'export_rate']: costWindow.rate.toString(), // Convert number to string for consistency
        start_timestamp_utc: costWindow.start_timestamp_utc,
      });

      // Collect all rates for fallback calculations
      accumulator.allRates.push(costWindow.rate);

      return accumulator;
    },
    { analyticsData: [] as AnalyticsRecord[], allRates: [] as number[] }
  );

  // Step 2: Use the existing utility to categorise rates as CHEAP, DAY, or PEAK
  // This utility is used throughout the app for consistent rate categorisation

  const categorisedData = addRateCategoriesToRecords(
    analyticsData,
    rateType // Specify whether we're processing import or export rates
  ) as AnalyticsRecord[];

  // Step 3: Extract rates by category in a single pass
  // This categorises each rate into cheap, day, or peak buckets based on the categorisation
  const { cheapRates, dayRates, peakRates } = categorisedData.reduce(
    (rateCategories, analyticsRecord) => {
      // Extract the numeric rate value from the record
      const rateValue = parseFloat(analyticsRecord[rateKey]!);

      // Get the category that was assigned by addRateCategoriesToRecords
      // Add this near your other type definitions
      const assignedCategory = analyticsRecord[rateCategoryKey];

      // Sort the rate into the appropriate category bucket
      if (assignedCategory === RateCategories.CHEAP) {
        rateCategories.cheapRates.push(rateValue);
      } else if (assignedCategory === RateCategories.DAY) {
        rateCategories.dayRates.push(rateValue);
      } else if (assignedCategory === RateCategories.PEAK) {
        rateCategories.peakRates.push(rateValue);
      }

      return rateCategories;
    },
    {
      cheapRates: [] as number[],
      dayRates: [] as number[],
      peakRates: [] as number[],
    }
  );

  // Step 4: Get unique rates for fallback calculations
  const uniqueRates = [...new Set(allRates)]; // Remove duplicates

  const dayFallback = allRates.reduce((sum, rate) => sum + rate, 0) / allRates.length;
  const cheapFallback = Math.min(...uniqueRates);
  const peakFallback = Math.max(...uniqueRates);

  // Step 5: Calculate the final tariff rates
  return {
    // Day rate: average of all day rates, or average of all rates if no day rates found
    day: {
      min: dayRates.length > 0 ? Math.min(...dayRates) : dayFallback,
      max: dayRates.length > 0 ? Math.max(...dayRates) : dayFallback,
    },
    // Cheap rate: minimum of all cheap rates, or minimum of all rates if no cheap rates found
    cheap: {
      min: cheapRates.length > 0 ? Math.min(...cheapRates) : cheapFallback,
      max: cheapRates.length > 0 ? Math.max(...cheapRates) : cheapFallback,
    },

    // Peak rate: maximum of all peak rates, or maximum of all rates if no peak rates found
    peak: {
      min: peakRates.length > 0 ? Math.min(...peakRates) : peakFallback,
      max: peakRates.length > 0 ? Math.max(...peakRates) : peakFallback,
    },
  };
};

/**
 * Processes import tariff cost windows to extract cheap, day, and peak rates
 *
 * This is a convenience wrapper around the generic processTariffRates function
 *
 * @param costWindows - Array of cost windows from the API
 * @returns Processed tariff rates or null if no data
 */
const processImportTariffRates = (costWindows: CostWindow[]): TariffRates | null => {
  return processTariffRates(costWindows, 'import');
};

/**
 * Processes export tariff cost windows to extract cheap, day, and peak rates
 *
 * This is a convenience wrapper around the generic processTariffRates function
 *
 * @param costWindows - Array of cost windows from the API
 * @returns Processed tariff rates or null if no data
 */
const processExportTariffRates = (costWindows: CostWindow[]): TariffRates | null => {
  return processTariffRates(costWindows, 'export');
};
